import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Download,
  Upload,
  Pencil,
  Trash,
  Calculator,
  Radical,
} from "lucide-react";

// UI Components
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DeleteModal } from "@/components/delete-modal";
import { Pagination } from "@/components/pagination";

// Hooks & Utils
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";

// Types & Forms
import { CombustionFactor, FuelType } from "@/types";
import { CombustionFactorForm } from "./CombustionFactorForm";
import { toast } from "react-toastify";
import { PageHeader } from "@/components/PageHeader";
import CombustionIcon from "@/components/icons/CombustionIcon";

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CombustionFactor[];
}

interface FuelTypeResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FuelType[];
}

export function CombustionFactorList() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedYear, setSelectedYear] = useState<string>("none");
  const [selectedFuelType, setSelectedFuelType] = useState<string>("none");
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const PAGE_SIZE = 10;

  // Handle file import
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        getApiUrl("combustion-factors/import_data/"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to import combustion factors");
      }

      // Invalidate and refetch
      await queryClient.invalidateQueries({
        queryKey: ["combustion-factors"],
      });

      // Show success message with import stats
      toast.success(
        t("common.importSuccess", {
          created: result.created,
          skipped: result.skipped,
          errors: result.errors || 0,
        }),
      );
    } catch (error) {
      console.error("Import failed:", error);
      toast.error(t("common.importError"));
    } finally {
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      // Build query parameters based on current filters
      const params = new URLSearchParams();

      if (selectedYear !== "none") {
        params.append("year", selectedYear);
      }

      if (selectedFuelType !== "none") {
        params.append("fuel_type", selectedFuelType);
      }

      // Add pagination parameters
      params.append("limit", "99999"); // Export all records

      // Create the export URL
      const exportUrl = `${getApiUrl(`combustion-factors/export/`)}?${params.toString()}`;

      // Make the API request with proper headers
      const response = await fetch(exportUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the response as a blob
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = `combustion_factors_export_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);

      // Trigger the download
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // Fetch fuel types for the filter
  const { data: fuelTypesData } = useQuery<FuelTypeResponse>({
    queryKey: ["fuel-types"],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl("fuel-types/?limit=9999&category=combustion"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch fuel types");
      }
      return response.json();
    },
    enabled: !!token,
  });

  const { data, isLoading, error } = useQuery<ApiResponse>({
    queryKey: [
      "combustion-factors",
      currentPage,
      selectedYear,
      selectedFuelType,
    ],
    queryFn: async () => {
      const offset = (currentPage - 1) * PAGE_SIZE;
      let url = `combustion-factors/?limit=${PAGE_SIZE}&offset=${offset}`;

      if (selectedYear !== "none") {
        url += `&year=${selectedYear}`;
      }

      if (selectedFuelType !== "none") {
        url += `&fuel_type=${selectedFuelType}`;
      }

      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch combustion factors");
      }
      return response.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(getApiUrl(`combustion-factors/${id}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete combustion factor");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["combustion-factors"] });
      toast.success(t("common.success.delete"));
      setItemToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(t("common.error"));
      setItemToDelete(null);
    },
  });

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleFuelTypeChange = (fuelType: string) => {
    setSelectedFuelType(fuelType);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setEditingId(null);
    setIsDrawerOpen(true);
  };

  if (error) {
    return <div className="text-destructive">Error: {error.message}</div>;
  }

  return (
    <div className="flex flex-col h-full pb-8">
      <PageHeader
        icon={
          <CombustionIcon
            width={180}
            height={180}
            className="w-[50px] h-[50px]"
          />
        }
        actionTitle="combustion.add"
        title="combustion.title"
        description="combustion.description"
        handleNew={handleAdd}
      >
        <Button
          variant="outline"
          onClick={handleExport}
          className="flex items-center"
        >
          <Download className="h-4 w-4 mr-2" />
          {t("common.export")}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          className="hidden"
          disabled={isImporting}
        />
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center"
          disabled={isImporting}
        >
          {isImporting ? (
            <>
              <span className="h-4 w-4 mr-2 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              {t("common.importing")}
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              {t("common.import")}
            </>
          )}
        </Button>
      </PageHeader>

      <div className="flex items-center space-x-2 p-4">
        <div className="flex flex-col space-y-2">
          <Select value={selectedYear} onValueChange={handleYearChange}>
            <SelectTrigger>
              <SelectValue placeholder={t("filters.selectYear")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("filters.allYears")}</SelectItem>
              {Array.from({ length: 20 }, (_, i) => {
                const year = new Date().getFullYear() - 10 + i;
                return (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col space-y-2">
          <Select value={selectedFuelType} onValueChange={handleFuelTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder={t("filters.selectFuelType")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("filters.allFuelTypes")}</SelectItem>
              {!fuelTypesData ? (
                <div className="p-2 text-sm text-muted-foreground">
                  {t("common.loading")}...
                </div>
              ) : fuelTypesData.results && fuelTypesData.results.length > 0 ? (
                fuelTypesData.results.map((fuelType) => (
                  <SelectItem key={fuelType.id} value={fuelType.id.toString()}>
                    {fuelType.name}
                  </SelectItem>
                ))
              ) : (
                <div className="p-2 text-sm text-muted-foreground">
                  {t("common.no_data_available")}
                </div>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("coefficients.combustion.fields.year")}
                </TableHead>
                <TableHead>
                  {t("coefficients.combustion.fields.fuelType")}
                </TableHead>
                <TableHead>
                  {t("coefficients.combustion.fields.coefficient")}
                </TableHead>
                <TableHead className="text-right">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-[80px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[150px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[100px]" />
                      </TableCell>

                      <TableCell>
                        <div className="flex justify-end space-x-2">
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                : data?.results.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell variant="compact" className="font-medium">
                        {item.year}
                      </TableCell>
                      <TableCell variant="compact">
                        {typeof item.fuel_type === "object"
                          ? item.fuel_type.name
                          : item.fuel_type || "-"}
                      </TableCell>
                      <TableCell variant="compact" className="text-center">
                        {item.coefficient}
                      </TableCell>
                      <TableCell variant="compact" className="text-right">
                        <div className="flex justify-center items-center">
                          <div
                            role="button"
                            className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                            onClick={() => handleEdit(item.id)}
                          >
                            <Pencil
                              size={30}
                              fill="#000000"
                              stroke="#ffffff"
                              strokeWidth={1}
                            />
                          </div>
                          <div
                            role="button"
                            className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                            onClick={() => setItemToDelete(item.id)}
                          >
                            <Trash
                              size={30}
                              fill="#ff0000"
                              stroke="#ffffff"
                              strokeWidth={2}
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
        {data && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            hasNextPage={!!data.next}
            hasPreviousPage={!!data.previous}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsDrawerOpen(false);
            setEditingId(null);
          }
        }}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingId
                ? t("coefficients.combustion.editTitle")
                : t("coefficients.combustion.addTitle")}
            </DrawerTitle>
            <DrawerDescription>
              {editingId
                ? t("coefficients.combustion.editDescription")
                : t("coefficients.combustion.addDescription")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-6">
            <CombustionFactorForm
              id={editingId || undefined}
              onSuccess={() => {
                setIsDrawerOpen(false);
                setEditingId(null);
                toast.success(
                  editingId
                    ? t("coefficients.combustion.updateSuccess")
                    : t("coefficients.combustion.createSuccess"),
                );
              }}
              onCancel={() => {
                setIsDrawerOpen(false);
                setEditingId(null);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>

      <DeleteModal
        open={itemToDelete !== null}
        onOpenChange={(open) => !open && setItemToDelete(null)}
        entity={t("coefficients.combustion.entityName")}
        onDelete={() => deleteMutation.mutate(itemToDelete!)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
